(() => {
  const MOBILE_QUERY = '(max-width: 749px)';
  const HEADER_SELECTOR = '.section-header';
  const ANNOUNCEMENT_SELECTOR = '.announcement-bar-section';
  const GALLERY_SELECTOR = "product-info[id^='MainProduct-'] .om-product-gallery";

  const initializeGallerySnap = (gallery) => {
    const mediaList = gallery.querySelector('[id^="Slider-Gallery"]');
    if (!mediaList || mediaList.dataset.omImmersiveSnap === 'true') return;

    const mobile = window.matchMedia(MOBILE_QUERY);
    let settleFrame = null;
    let stableFrames = 0;
    let lastScrollLeft = mediaList.scrollLeft;
    let correcting = false;

    mediaList.dataset.omImmersiveSnap = 'true';

    const getSlides = () =>
      Array.from(mediaList.querySelectorAll(':scope > .product__media-item')).filter(
        (slide) => slide.clientWidth > 0,
      );

    const snapToNearestSlide = () => {
      if (!mobile.matches || correcting) return;

      const slides = getSlides();
      if (slides.length < 2) return;

      const listRect = mediaList.getBoundingClientRect();
      let nearestSlide = slides[0];
      let nearestDistance = Number.POSITIVE_INFINITY;

      slides.forEach((slide) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - listRect.left);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestSlide = slide;
        }
      });

      const slideOffset = nearestSlide.getBoundingClientRect().left - listRect.left;
      const maximumScroll = Math.max(0, mediaList.scrollWidth - mediaList.clientWidth);
      const target = Math.max(
        0,
        Math.min(maximumScroll, mediaList.scrollLeft + slideOffset),
      );

      if (Math.abs(target - mediaList.scrollLeft) <= 0.5) return;

      correcting = true;
      const previousBehavior = mediaList.style.scrollBehavior;
      mediaList.style.scrollBehavior = 'auto';
      mediaList.scrollLeft = target;

      window.requestAnimationFrame(() => {
        mediaList.style.scrollBehavior = previousBehavior;
        correcting = false;
      });
    };

    const watchForSettlement = () => {
      if (!mobile.matches || correcting) {
        settleFrame = null;
        return;
      }

      const currentScrollLeft = mediaList.scrollLeft;
      if (Math.abs(currentScrollLeft - lastScrollLeft) <= 0.25) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
      }
      lastScrollLeft = currentScrollLeft;

      if (stableFrames >= 3) {
        settleFrame = null;
        snapToNearestSlide();
        return;
      }

      settleFrame = window.requestAnimationFrame(watchForSettlement);
    };

    const requestSettlementCheck = () => {
      if (settleFrame !== null) return;
      stableFrames = 0;
      lastScrollLeft = mediaList.scrollLeft;
      settleFrame = window.requestAnimationFrame(watchForSettlement);
    };

    if ('onscrollend' in mediaList) {
      mediaList.addEventListener('scrollend', snapToNearestSlide, { passive: true });
    }

    /* Samsung Browser has shipped versions where CSS snap ends a few pixels
       short. The settled-frame check is kept even when scrollend is exposed. */
    mediaList.addEventListener('scroll', requestSettlementCheck, { passive: true });
    mediaList.addEventListener('touchend', requestSettlementCheck, { passive: true });
    mediaList.addEventListener('pointerup', requestSettlementCheck, { passive: true });

    const alignAfterLayout = () =>
      window.requestAnimationFrame(() => window.requestAnimationFrame(snapToNearestSlide));

    if (mobile.addEventListener) {
      mobile.addEventListener('change', alignAfterLayout);
    } else {
      mobile.addListener(alignAfterLayout);
    }

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(alignAfterLayout);
      resizeObserver.observe(mediaList);
    }

    alignAfterLayout();
  };

  const initialize = () => {
    const header = document.querySelector(HEADER_SELECTOR);
    const announcement = document.querySelector(ANNOUNCEMENT_SELECTOR);
    const gallery = document.querySelector(GALLERY_SELECTOR);
    if (!gallery) return;

    initializeGallerySnap(gallery);
    if (!header || header.dataset.omImmersivePdp === 'true') return;

    const mobile = window.matchMedia(MOBILE_QUERY);
    let frame = null;
    let wasSolid = false;

    header.dataset.omImmersivePdp = 'true';
    header.classList.add('om-pdp-header');

    const syncInteractionState = () => {
      const interactionOpen =
        header.classList.contains('menu-open') ||
        Boolean(header.querySelector('details[open]'));

      header.classList.toggle(
        'om-pdp-header--interaction-open',
        mobile.matches && interactionOpen,
      );
    };

    const update = () => {
      frame = null;

      if (!mobile.matches) {
        wasSolid = false;
        document.documentElement.style.removeProperty(
          '--om-pdp-announcement-visible',
        );
        header.classList.remove(
          'om-pdp-header--solid',
          'om-pdp-header--interaction-open',
        );
        return;
      }

      const announcementVisible = announcement
        ? Math.max(0, announcement.getBoundingClientRect().bottom)
        : 0;

      document.documentElement.style.setProperty(
        '--om-pdp-announcement-visible',
        `${announcementVisible}px`,
      );

      document.documentElement.style.setProperty(
        '--om-immersive-header-height',
        `${header.offsetHeight}px`,
      );

      const solid = gallery.getBoundingClientRect().bottom <= header.offsetHeight + 8;
      header.classList.toggle('om-pdp-header--solid', solid);

      if (solid && !wasSolid) {
        header.classList.add('shopify-section-header-sticky');
        header.classList.remove('shopify-section-header-hidden');
      } else if (!solid && wasSolid) {
        header.classList.remove(
          'shopify-section-header-hidden',
          'shopify-section-header-sticky',
          'scrolled-past-header',
          'animate',
        );
      }

      if (!solid) {
        header.classList.remove(
          'shopify-section-header-hidden',
          'shopify-section-header-sticky',
          'scrolled-past-header',
          'animate',
        );
      }

      wasSolid = solid;
      syncInteractionState();
    };

    const requestUpdate = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    header.addEventListener('toggle', () => {
      window.requestAnimationFrame(syncInteractionState);
    }, true);

    if (mobile.addEventListener) {
      mobile.addEventListener('change', requestUpdate);
    } else {
      mobile.addListener(requestUpdate);
    }

    const observer = new MutationObserver(syncInteractionState);
    observer.observe(header, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: false,
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(requestUpdate);
      resizeObserver.observe(header);
      resizeObserver.observe(gallery);
      if (announcement) resizeObserver.observe(announcement);
    }

    update();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();
